/**
 * @copyright Copyright 2020 Epic Systems Corporation
 * @file Component To Handle Grouping in Call Video Feeds
 * @author Will Cooper
 * @module Epic.VideoApp.Components.Participants.ParticipantSection
 */

import React, { FC, ReactElement, useEffect, useState } from "react";
import { resolveClassName } from "~/utils/className";
import MainParticipant from "./MainParticipant";
import styles from "./ParticipantSection.module.scss";
import VideoStrip from "./VideoStrip";

/**
 * The ParticipantSection component
 */
const ParticipantSection: FC = (): ReactElement | null => {
	// For a vertical display want to wrap the Main Participant and the Strip in one div to allow them to be side by side in flex
	// On smaller screens we want to stack them, so we follow the overlaying flex column designation

	const [, setOrientation] = useState(false);

	useEffect(() => {
		const handleOrientationChange = (): void => {
			setTimeout(() => {
				setOrientation((prevState) => !prevState);
			}, 20);
		};

		window.addEventListener("orientationchange", handleOrientationChange);

		return () => {
			window.removeEventListener("orientationchange", handleOrientationChange);
		};
	}, []);

	const className = resolveClassName(styles, { participantView: true });

	return (
		<div className={className} data-testid="ParticipantSection" id="participantView">
			<MainParticipant />
			<VideoStrip />
		</div>
	);
};

export default ParticipantSection;
